import { BASE_XP_PER_POINT, LEVEL_SCALING_PERCENT, SocialParameterName } from '../constants/socialParameters';

export interface PointsDistribution {
  [parameterName: string]: number;
}

export interface XPBreakdown {
  parameterXP: { parameter: SocialParameterName; points: number; xp: number }[];
  totalParameterXP: number;
  userXP: number;
  domainXP: number;
  bondXP: number;
  totalXP: number;
}

export function calculatePointsAvailable(durationMinutes: number): number {
  const points = Math.round((durationMinutes / 60) * 3);
  return Math.max(1, Math.min(12, points));
}

export function calculateXpPerPoint(socialDomainLevel: number): number {
  const scalingMultiplier = 1 + (socialDomainLevel * LEVEL_SCALING_PERCENT) / 100;
  return Math.round(BASE_XP_PER_POINT * scalingMultiplier);
}

export function calculateActivityXP(
  durationMinutes: number,
  pointsDistribution: PointsDistribution,
  socialDomainLevel: number = 1
): XPBreakdown {
  const xpPerPoint = calculateXpPerPoint(socialDomainLevel);

  const parameterXP: { parameter: SocialParameterName; points: number; xp: number }[] = [];
  let totalParameterXP = 0;

  for (const [parameter, points] of Object.entries(pointsDistribution)) {
    if (points > 0) {
      const xp = points * xpPerPoint;
      parameterXP.push({
        parameter: parameter as SocialParameterName,
        points,
        xp
      });
      totalParameterXP += xp;
    }
  }

  const bondXP = Math.round(totalParameterXP * 0.5);
  const userXP = Math.round(totalParameterXP * 0.25);
  const domainXP = Math.round(totalParameterXP * 0.25);

  return {
    parameterXP,
    totalParameterXP,
    userXP,
    domainXP,
    bondXP,
    totalXP: totalParameterXP + bondXP + userXP + domainXP
  };
}

export function validatePointsDistribution(
  pointsDistribution: PointsDistribution,
  pointsAvailable: number
): { valid: boolean; totalUsed: number; remaining: number } {
  const totalUsed = Object.values(pointsDistribution).reduce((sum, points) => sum + points, 0);
  return {
    valid: totalUsed <= pointsAvailable,
    totalUsed,
    remaining: pointsAvailable - totalUsed
  };
}

export function getEmptyPointsDistribution(): PointsDistribution {
  return {
    'Communication': 0,
    'Emotional Intelligence': 0,
    'Trust & Reliability': 0,
    'Boundaries': 0,
    'Social Confidence': 0,
    'Conflict Navigation': 0,
    'Relationship Depth': 0
  };
}

export function formatXPGain(xp: number): string {
  if (xp >= 1000) {
    return `${(xp / 1000).toFixed(1)}k`;
  }
  return xp.toString();
}

export function estimateInteractionsToNextRank(
  currentXP: number,
  targetXP: number,
  avgPointsPerActivity: number = 3,
  socialDomainLevel: number = 1
): number {
  const xpNeeded = targetXP - currentXP;
  if (xpNeeded <= 0) return 0;

  const xpPerPoint = calculateXpPerPoint(socialDomainLevel);
  const xpPerActivity = avgPointsPerActivity * xpPerPoint * 0.5;

  return Math.ceil(xpNeeded / xpPerActivity);
}
